/**
 * Memory API
 * API calls for managing agent memories
 * Uses userApiClient for /v1/agents endpoints
 */

import userApiClient from './userClient';

export interface Memory {
  id: string;
  memory: string;
  hash?: string;
  topics?: string[];
  metadata?: Record<string, any>;
  created_at?: string;
  updated_at?: string;
  last_updated?: string;
  user_id?: string;
  agent_id?: string;
}

export interface MemorySearchResult {
  memories: Memory[];
  query: string;
  method: string;
  total_found: number;
  agent_type: string;
}

// Memory scope options
export type MemoryScope = 'all' | 'user' | 'agent';

// Category type options
export type CategoryType = 'all' | 'permanent' | 'temporal';

// Query options for list and search
export interface MemoryQueryOptions {
  limit?: number;
  offset?: number;
  memory_scope?: MemoryScope;
  category_type?: CategoryType;
}

export const memoryApi = {
  /**
   * Get all memories for an agent with optional filtering
   * Backend returns List[MemoryResponse] directly
   */
  list: async (agentType: string, options: MemoryQueryOptions = {}): Promise<Memory[]> => {
    const { limit = 100, offset = 0, memory_scope = 'all', category_type = 'all' } = options;
    const { data } = await userApiClient.get(`/agents/${agentType}/memories`, {
      params: { limit, offset, memory_scope, category_type },
    });
    // Backend returns array directly
    return Array.isArray(data) ? data : (data.memories || []);
  },

  /**
   * Get a specific memory by ID
   */
  get: async (agentType: string, memoryId: string): Promise<Memory> => {
    const { data } = await userApiClient.get(`/agents/${agentType}/memories/${memoryId}`);
    return data;
  },

  /**
   * Search memories with optional filtering
   */
  search: async (
    agentType: string,
    query: string,
    options: Pick<MemoryQueryOptions, 'memory_scope' | 'category_type'> = {}
  ): Promise<MemorySearchResult> => {
    const { memory_scope = 'all', category_type = 'all' } = options;
    const { data } = await userApiClient.post(`/agents/search`, {
      query,
      agent_type: agentType,
      memory_scope,
      category_type,
    });
    return data;
  },

  /**
   * Delete a memory
   */
  delete: async (agentType: string, memoryId: string): Promise<void> => {
    await userApiClient.delete(`/agents/${agentType}/memories/${memoryId}`);
  },

  /**
   * Delete all memories for an agent
   */
  deleteAll: async (agentType: string): Promise<void> => {
    await userApiClient.delete(`/agents/${agentType}/memories`);
  },

  /**
   * Add a memory manually
   */
  add: async (agentType: string, content: string, metadata?: Record<string, any>): Promise<Memory> => {
    const { data } = await userApiClient.post(`/agents/${agentType}/memories`, {
      content,
      metadata,
    });
    return data;
  },
};

export default memoryApi;
